import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import {
  LayoutDashboard,
  LogOut,
  Settings,
  ShieldCheck,
  UserCog,
  ClipboardList,
  SlidersHorizontal,
} from 'lucide-react'
import {
  AppShell,
  Avatar,
  Icon,
  Sidebar,
  SidebarDrawer,
  Topbar,
  useToast,
} from '@navegasistemas/dls-navega-rjs'
import { useMutation } from '@tanstack/react-query'
import { authService } from '@/features/auth/services/auth.service'
import { useAuthStore } from '@/store/auth-store'
import { useAuth } from '@/hooks/use-auth'
import { PERMISSIONS } from '@/lib/permissions'
import { navigationGuard } from '@/lib/navigation-guard'

const MOBILE_BP = 768
const TABLET_BP = 1024

function getScreenClass() {
  const w = typeof window !== 'undefined' ? window.innerWidth : TABLET_BP
  if (w < MOBILE_BP) return 'mobile' as const
  if (w < TABLET_BP) return 'tablet' as const
  return 'desktop' as const
}

const NAV_GROUPS: {
  label: string
  items: { key: string; label: string; icon: React.ReactNode; permission?: string }[]
}[] = [
  {
    label: 'Geral',
    items: [{ key: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard /> }],
  },
  {
    label: 'Gestão',
    items: [
      { key: '/users', label: 'Usuários', icon: <UserCog />, permission: PERMISSIONS.USER_LIST },
      {
        key: '/users/import',
        label: 'Importação em lote',
        icon: <Icon name="upload" />,
        permission: PERMISSIONS.USER_IMPORT,
      },
      {
        key: '/users/audit',
        label: 'Auditoria',
        icon: <ClipboardList />,
        permission: PERMISSIONS.AUDIT_READ,
      },
      {
        key: '/roles',
        label: 'Perfis de acesso',
        icon: <ShieldCheck />,
        permission: PERMISSIONS.ROLE_READ,
      },
    ],
  },
  {
    label: 'Configurações',
    items: [
      {
        key: '/parameters',
        label: 'Parâmetros',
        icon: <SlidersHorizontal />,
        permission: PERMISSIONS.PARAMETERS_READ,
      },
    ],
  },
]

const BREADCRUMB_LABELS: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/roles': 'Perfis de acesso',
  '/roles/new': 'Novo grupo',
  '/users': 'Usuários',
  '/users/new': 'Novo usuário',
  '/users/import': 'Importação em lote',
  '/users/audit': 'Auditoria',
  '/preferences': 'Preferências',
  '/parameters': 'Parâmetros',
}

function getBreadcrumbItems(pathname: string): { label: string }[] {
  if (/^\/users\/.+/.test(pathname)) {
    return [{ label: 'Usuários' }, { label: BREADCRUMB_LABELS[pathname] ?? 'Detalhes' }]
  }
  if (/^\/roles\/.+/.test(pathname)) {
    return [{ label: 'Perfis de acesso' }, { label: BREADCRUMB_LABELS[pathname] ?? 'Detalhes' }]
  }
  return [{ label: BREADCRUMB_LABELS[pathname] ?? 'Início' }]
}

function getActiveKey(pathname: string): string {
  if (pathname.startsWith('/users/')) {
    if (pathname === '/users/import') return '/users/import'
    if (pathname === '/users/audit') return '/users/audit'
    return '/users'
  }
  if (pathname.startsWith('/roles/')) return '/roles'
  return pathname
}

export function AppLayout() {
  const [screen, setScreen] = useState(getScreenClass)
  const [collapsed, setCollapsed] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { toast } = useToast()
  const { accessToken, refreshToken, clearAuth } = useAuthStore()
  const { user, can } = useAuth()

  useEffect(() => {
    function handleResize() {
      const next = getScreenClass()
      setScreen(next)
      if (next !== 'mobile') setDrawerOpen(false)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const { mutate: logout } = useMutation({
    mutationFn: () =>
      refreshToken ? authService.logout({ refreshToken }, accessToken!) : Promise.resolve(),
    onSettled: () => {
      clearAuth()
      navigate('/login')
    },
    onError: () => toast({ variant: 'danger', title: 'Erro ao sair', message: 'Tente novamente.' }),
  })

  const navGroups = NAV_GROUPS.flatMap((group) => {
    const visibleItems = group.items
      .filter((item) => !item.permission || can(item.permission))
      .map((item) => ({
        ...item,
        onClick: () => {
          navigationGuard.attempt(() => {
            navigate(item.key)
            setDrawerOpen(false)
          })
        },
      }))
    return visibleItems.length > 0 ? [{ ...group, items: visibleItems }] : []
  })

  const footerItems = [
    {
      key: '/preferences',
      label: 'Preferências',
      icon: <Settings />,
      onClick: () => {
        navigate('/preferences')
        setDrawerOpen(false)
      },
    },
    {
      key: 'logout',
      label: 'Sair',
      icon: <LogOut />,
      onClick: () => logout(),
    },
  ]

  const logo = (
    <span className="text-sm font-semibold text-primary-700 tracking-tight">
      {screen === 'mobile' || collapsed ? 'N' : 'Navega Plataforma'}
    </span>
  )

  const sharedSidebarProps = {
    groups: navGroups,
    activeKey: getActiveKey(pathname),
    logo,
    footerItems,
  }

  const isMobile = screen === 'mobile'
  const isTablet = screen === 'tablet'

  const drawerEl = (
    <SidebarDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} {...sharedSidebarProps} />
  )

  const persistentSidebar = isMobile ? undefined : (
    <Sidebar
      {...sharedSidebarProps}
      collapsed={isTablet ? true : collapsed}
      onCollapsedChange={isTablet ? undefined : setCollapsed}
    />
  )

  function handleMenuToggle() {
    if (isMobile) {
      setDrawerOpen((o) => !o)
    } else if (!isTablet) {
      setCollapsed((c) => !c)
    }
  }

  const sidebarCollapsed = isTablet ? true : collapsed

  useEffect(() => {
    const width = isMobile ? '0px' : sidebarCollapsed ? '4rem' : '15rem'
    document.documentElement.style.setProperty('--sidebar-width', width)
    return () => {
      document.documentElement.style.removeProperty('--sidebar-width')
    }
  }, [isMobile, sidebarCollapsed])

  return (
    <>
      {isMobile && drawerEl}
      <AppShell
        sidebarCollapsed={sidebarCollapsed}
        sidebar={persistentSidebar}
        topbar={
          <Topbar
            breadcrumb={getBreadcrumbItems(pathname)}
            onMenuToggle={handleMenuToggle}
            searchSlot={null}
            rightSlot={<Avatar name={user?.name ?? user?.email ?? 'Usuário'} size="sm" />}
          />
        }
      >
        <Outlet />
      </AppShell>
    </>
  )
}
